import { DrawerProps, Tooltip } from 'antd';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import { CloseOutlined } from '@ant-design/icons';
import { Attention as AttentionIcon } from '@dynamika-shared/ui/icons';
import { CohortType } from '@dynamika-shared/types/cohorts';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { VisitConfig, VisitConfigCreate } from '@dynamika-shared/types/visits';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { SelectLabels } from '@components/SelectLabels/SelectLabels';
import { Option } from '@utils/generateOptions';
import { SelectedItems } from '@components/SelectedItems/SelectedItems';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ModalityLabel } from '@dynamika-shared/types/imagingLabels';
import { twJoin } from '@utils/twMerge';
import { VisitMode } from './ClinicalTrialVisits';
import _ from 'lodash';
import { CommonInventoryType } from '@dynamika-shared/types/inventory';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { UnsavedChangesModal } from '@components/modals/UnsavedChangesModal/UnsavedChangesModal';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { searchByName } from 'src/pages/ClinicalTrial/ClinicalTrialVisits/utils/searchByName';

type CreateVisitProps = DrawerProps & {
  close?: () => void;
  setShouldBeExpand?: (id: string) => void;
  cohorts: CohortType[];
  currentCohort?: CohortType;
  currentVisit?: VisitConfig;
  visitMode?: VisitMode;
};

const visitLabels = (inputArray: ModalityLabel[], idsToFind: string[] = []) => {
  const idsToFindObject = _.keyBy(idsToFind);
  return _(inputArray)
    .flatMap('labels')
    .reduce((accumulator: Option[], { id, name }: CommonInventoryType) => {
      if (idsToFindObject[id]) {
        accumulator.push({ value: id, label: name, id, name } as Option);
      }
      return accumulator;
    }, []);
};

const schema = yup.object().shape({
  name: yup
    .string()
    .max(50, 'Visit Name should not exceed 50 characters')
    .required('Visit Name is required')
    .matches(
      /^[a-zA-Z0-9\s]+$/,
      'Only alphanumeric characters and spaces are allowed'
    ),
  cohort: yup.string().required(),
  daysFromBaseline: yup
    .number()
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === '' ? null : value;
    })
    .typeError('Days from Baseline must be a number')
    .required('Days from Baseline is required'),
  labels: yup.array().min(1).required(),
});

const Helper: FC = () => {
  const tooltipTitle = (
    <div className='text-chineseBlack'>
      <div>
        To add a <b>Baseline</b> visit, create a visit and set it as '0 days
        from baseline
      </div>
    </div>
  );
  return (
    <Tooltip color='#F0F5FF' title={tooltipTitle}>
      <AttentionIcon className='ml-[.5rem] cursor-pointer' />
    </Tooltip>
  );
};

const numericInputPattern = /^-?\d*$/;

export const CreateVisit: FC<CreateVisitProps> = ({
  close,
  cohorts,
  currentCohort,
  setShouldBeExpand = () => {},
  visitMode,
  currentVisit,
}) => {
  const { id: clinicalTrialId } = useParams<{
    id: string;
  }>();
  const [labels, loading, createVisit, updateVisit] = useClinicalTrialSlice(
    (state, actions) => [
      state.data?.labelGroups ?? [],
      state.clinicalTrialActionLoading,
      actions.createVisitRequest,
      actions.updateVisitRequest,
    ]
  );

  const existedLabels = useMemo(() => {
    return visitLabels(labels, currentVisit?.labels) || [];
  }, [currentVisit?.labels, labels]);
  const [currentLabels, setCurrentLabels] = useState(labels);

  const defaultValues = {
    cohort: currentCohort?.id,
    name: currentVisit?.name ?? '',
    daysFromBaseline: currentVisit?.daysFromBaseline ?? undefined,
    labels: currentVisit?.labels ?? [],
  };

  const { watch, setValue, trigger, formState, getValues } =
    useForm<VisitConfigCreate>({
      resolver: yupResolver(schema),
      defaultValues,
    });
  const { errors, isValid, isSubmitting, isDirty, touchedFields } = formState;
  const [selectedItems, setSelectedItems] = useState<Option[]>(existedLabels);
  const [searchValue, setSearchValue] = useState<string>('');
  const [askQuit, setAskQuit] = useState<boolean>(false);
  const [targetCohort, setTargetCohort] = useState(currentCohort);

  const handleTagClose = (removedItem: Option) => {
    const updatedItems = selectedItems.filter(item => item !== removedItem);
    setSelectedItems(updatedItems);
  };

  const search = (searchTerm: string) => {
    setCurrentLabels(searchByName(labels, searchTerm));
  };

  const submit = async () => {
    if (clinicalTrialId) {
      createVisit({
        clinicalTrialId,
        data: [getValues()],
        onSuccess: () => {
          setShouldBeExpand(targetCohort?.id ?? '');
          close && close();
        },
      });
    }
  };

  const update = async () => {
    if (clinicalTrialId && currentVisit?.id) {
      updateVisit({
        clinicalTrialId,
        visitId: currentVisit.id,
        data: getValues(),
        onSuccess: () => {
          setShouldBeExpand(targetCohort?.id ?? '');
          close && close();
        },
      });
    }
  };

  const onCloseDrawer = () => {
    if (visitMode == 'CREATE_VISIT') {
      if (!isDirty && !watch('labels').length) {
        close && close();
      } else {
        setAskQuit(true);
      }
    } else {
      const labelsEqual =
        !isDirty && _.isEqual(watch('labels'), currentVisit?.labels);
      if (labelsEqual) {
        close && close();
      } else {
        setAskQuit(true);
      }
    }
  };

  const onChangeField = async (
    fieldName: keyof VisitConfigCreate,
    value: string | number | string[]
  ) => {
    setValue(fieldName, value, { shouldDirty: true, shouldTouch: true });
    trigger(fieldName);
  };

  const onBlur = (fieldName: keyof VisitConfigCreate, value: string) => {
    if (touchedFields[fieldName]) {
      onChangeField(fieldName, value.trim());
    }
  };

  const updateForInteger = (
    fieldName: keyof VisitConfigCreate,
    value: string | number | undefined
  ) => {
    onChangeField(fieldName, value as string);
  };
  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!numericInputPattern.test(e.key ?? '')) {
      e.preventDefault();
    }
  };
  useEffect(() => {
    const labels = selectedItems.map(label => label.value);
    setValue('labels', labels, { shouldDirty: true, shouldTouch: true });
    trigger('labels');
  }, [selectedItems, setValue, trigger]);
  return (
    <Drawer
      classNames={{
        header:
          '!px-0 !pb-3 !pt-1 [&_button]:absolute [&_button]:top-[1.3rem] [&_button]:right-[0.4rem]',
        body: 'flex flex-col !px-0 !pb-0',
        content: 'rounded-lg',
      }}
      title={
        currentVisit ? (
          <div className='flex gap-1'>
            Update
            <CustomTooltip MAX_LABEL_LENGTH={10} label={currentVisit?.name} />
            Visit
          </div>
        ) : (
          'Add New Visit'
        )
      }
      open
      width={464}
      placement='right'
      onClose={onCloseDrawer}
      closeIcon={<CloseOutlined />}
      data-testid='addCohortModal'
    >
      <form className='overflow-auto'>
        <Select
          listHeight={160}
          options={cohorts.map(cohort => ({
            label: cohort.name,
            value: cohort.id,
          }))}
          onChange={value => {
            setValue('cohort', value);
            const foundCohort = cohorts.find(c => c.id === value);
            if (foundCohort) {
              setTargetCohort(foundCohort);
            }
          }}
          defaultValue={
            currentCohort
              ? {
                  label:
                    cohorts.find(c => c.id === currentCohort.id)?.name ??
                    currentCohort.name,
                  value: currentCohort.id,
                }
              : undefined
          }
          label={'Selected Cohort'}
          data-testid='cohort-visit'
          className={twJoin(
            'mb-3',
            (cohorts.length === 1 || visitMode === 'EDIT_VISIT') &&
              'pointer-events-none'
          )}
        />
        <Input
          required
          value={watch('name')}
          name={'name'}
          onChange={e => onChangeField('name', e.target.value)}
          onBlur={e => onBlur('name', e.target.value)}
          data-testid={'visit-name'}
          errors={errors.name?.message}
          label={'Visit Name'}
          placeholder={'Visit Name'}
          classNameInputWrapper='mb-3'
          maxLength={50}
        />
        <Input
          required
          value={watch('daysFromBaseline')}
          onChange={e => updateForInteger('daysFromBaseline', e.target.value)}
          onKeyUp={onKeyPress}
          name={'daysFromBaseline'}
          data-testid={'visit-daysFromBaseline'}
          errors={errors.daysFromBaseline?.message}
          label={'Days from Baseline'}
          placeholder={'Days from Baseline'}
          classNameInputWrapper='mb-3'
          extraHelper={<Helper />}
          maxLength={
            watch('daysFromBaseline')?.toString().includes('-') ? 5 : 4
          }
        />
        <SelectLabels
          searchValue={searchValue}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          onSearch={setSearchValue}
          ctLabels={[]}
          imagingLabels={currentLabels}
          fetchItems={search}
        />
        <SelectedItems
          selectedItems={selectedItems}
          onClose={handleTagClose}
          title={'Selected labels'}
        />
      </form>
      <footer className='mt-auto'>
        <hr className='mb-3' />
        <div className='flex justify-between'>
          <Button
            data-testid={'cancel-btn'}
            className='w-[17rem]'
            onClick={onCloseDrawer}
          >
            Cancel
          </Button>
          <Button
            data-testid={'ok-btn'}
            className='w-[17rem]'
            type='primary'
            disabled={!isValid || isSubmitting || !isDirty || loading}
            onClick={visitMode === 'EDIT_VISIT' ? update : submit}
          >
            {currentVisit ? 'Save' : 'Add'}
          </Button>
        </div>
      </footer>
      <UnsavedChangesModal
        onConfirm={() => close && close()}
        onCancel={() => setAskQuit(false)}
        open={askQuit}
      />
    </Drawer>
  );
};
